import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import Select from 'react-select';
import axios from '../../../../../services/AxiosInstance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faBuilding, faUser, faSearch, faMapMarkerAlt, faPhone, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { Building, User, Search, MapPin, Phone, Mail, Users, Plus, Trash2, X } from 'lucide-react';
import swal from 'sweetalert';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { useFormik, FormikProvider, FieldArray } from 'formik';
import * as Yup from 'yup';

const typeOptions = [
  { value: 'company', label: 'Företag' },
  { value: 'individual', label: 'Privatperson' },
];

const addressTypeOptions = [
  { value: 'billing', label: 'Fakturaadress' },
  { value: 'shipping', label: 'Leveransadress' },
  { value: 'other', label: 'Annan adress' },
];

// Validation Schema with conditions
const validationSchema = Yup.object().shape({
  type: Yup.string().required('Kundtyp måste väljas'),
  name: Yup.string().when('type', {
    is: 'company',
    then: Yup.string().required('Företagsnamn krävs för företag'),
    otherwise: Yup.string().notRequired(),
  }),
  orgnumber: Yup.string().when('type', {
    is: 'company',
    then: Yup.string().required('Organisationsnummer krävs för företag'),
    otherwise: Yup.string().notRequired(),
  }),
  first_name: Yup.string().when('type', {
    is: 'individual',
    then: Yup.string().required('Förnamn krävs för privatperson'),
    otherwise: Yup.string().notRequired(),
  }),
  last_name: Yup.string().when('type', {
    is: 'individual',
    then: Yup.string().required('Efternamn krävs för privatperson'),
    otherwise: Yup.string().notRequired(),
  }),
  phone: Yup.string().required('Telefonnummer är obligatoriskt'),
  payment_terms: Yup.number().transform(value => (isNaN(value) ? undefined : value))
    .nullable(true)
    .notRequired(),
  addresses: Yup.array().of(
    Yup.object().shape({
      address_type: Yup.string().oneOf(['billing', 'shipping', 'other']).required(),
      street1: Yup.string().required('Gatuadress krävs'),
      city: Yup.string().required('Ort krävs'),
      postalcode: Yup.string().required('Postnummer krävs'),
      country: Yup.string().nullable(),
    })
  ).min(1, 'Minst en adress måste anges'),
  contacts: Yup.array().when('type', {
    is: 'company',
    then: Yup.array().of(
      Yup.object().shape({
        contact_name: Yup.string().required('Kontaktpersonens namn krävs för företag'),
        contact_email: Yup.string().email('Ogiltig e-post').nullable(),
        contact_phone: Yup.string().nullable(),
        role: Yup.string().nullable(),
      })
    ).min(1, 'Minst en kontaktperson måste anges för ett företag'),
    otherwise: Yup.array().of(
      Yup.object().shape({
        contact_name: Yup.string().nullable(),
        contact_email: Yup.string().email('Ogiltig e-post').nullable(),
        contact_phone: Yup.string().nullable(),
        role: Yup.string().nullable(),
      })
    )
  })
});

const NewCustomerModal = ({ show, handleClose, handleSave, handleUpdate, customerDetails }) => {
  const isEditMode = !!customerDetails;

  const defaultValues = {
    id: '',
    type: 'company',
    orgnumber: '',
    vatnumber: '',
    name: '',
    first_name: '',
    last_name: '',
    phone: '',
    payment_terms: '',
    addresses: [
      {
        address_type: 'billing',
        street1: '',
        street2: '',
        city: '',
        postalcode: '',
        country: '',
      },
    ],
    contacts: [
      {
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        role: '',
      },
    ],
  };

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        type: values.type,
        orgnumber: values.type === 'company' ? values.orgnumber || '' : '',
        vatnumber: values.type === 'company' ? values.vatnumber || '' : '',
        name: values.type === 'company' ? values.name || '' : '',
        first_name: values.type === 'individual' ? values.first_name || '' : '',
        last_name: values.type === 'individual' ? values.last_name || '' : '',
        phone: values.phone || '',
        payment_terms: values.payment_terms || '',
        addresses: values.addresses.map((addr) => ({
          address_type: addr.address_type,
          street1: addr.street1 || '',
          street2: addr.street2 || '',
          city: addr.city || '',
          postalcode: addr.postalcode || '',
          country: addr.country || '',
        })),
        contacts: values.contacts.map((ct) => ({
          contact_name: ct.contact_name || '',
          contact_email: ct.contact_email || '',
          contact_phone: ct.contact_phone || '',
          role: ct.role || '',
        })),
      };

      if (isEditMode) {
        payload.id = values.id;
        handleUpdate(payload);
      } else {
        handleSave(payload);
      }
      handleClose();
    }
  });

  const { values, errors, touched, setFieldValue } = formik;

  useEffect(() => {
    if (show) {
      if (isEditMode && customerDetails) {
        const {
          id,
          type,
          orgnumber,
          vatnumber,
          name,
          first_name,
          last_name,
          phone,
          payment_terms,
          addresses = [],
          contacts = [],
        } = customerDetails;

        formik.resetForm({
          values: {
            id: id || '',
            type: type || 'company',
            orgnumber: orgnumber || '',
            vatnumber: vatnumber || '',
            name: name || '',
            first_name: first_name || '',
            last_name: last_name || '',
            phone: phone || '',
            payment_terms: payment_terms || '',
            addresses: addresses.length > 0 ? addresses : [
              {
                address_type: 'billing',
                street1: '',
                street2: '',
                city: '',
                postalcode: '',
                country: '',
              },
            ],
            contacts: contacts.length > 0 ? contacts : [
              {
                contact_name: '',
                contact_email: '',
                contact_phone: '',
                role: '',
              },
            ],
          }
        });
      } else {
        formik.resetForm({ values: defaultValues });
      }
      setCompanies([]);
      setSelectedCompany(null);
    }
  }, [show, isEditMode, customerDetails]);

  const searchCompany = async (inputValue) => {
    if (!inputValue) return;

    setSearchLoading(true);
    try {
      const searchCompanyApiUrl = getApiUrl('generic/search-company');
      const response = await axios.get(searchCompanyApiUrl, {
        params: { query: inputValue },
      });

      setCompanies(
        response.data.hits.map((company) => ({
          label: `${company.document.names[0].nameOrIdentifier} (${company.document.registrationNumber})`,
          value: company,
        }))
      );
    } catch (err) {
      console.error('Error fetching company data:', err.message);
      swal(
        'Fel',
        'Kunde inte hämta företagsinformation, försök igen senare eller ange informationen manuellt.',
        'error'
      );
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCompanySelect = (selectedOption) => {
    const company = selectedOption.value;
    setSelectedCompany(company);

    const visitingAddress = company.document.addresses?.find(addr => addr.addressType === 'registeredAddress');
    const mailAddress = company.document.addresses?.find(addr => addr.addressType === 'mailAddress');
    const selectedAddress = visitingAddress || mailAddress || {};

    const phoneNumber = company.document.phoneNumbers?.[0]?.e164PhoneNumber || '';
    const email = company.document.emailAddresses?.[0].emailAddress || '';
    const compName = company.document.names?.[0]?.nameOrIdentifier || '';

    const updatedAddresses = [
      {
        address_type: 'billing',
        street1: ((selectedAddress.street || '') + ' ' + (selectedAddress.houseNumber || '')).trim(),
        street2: '',
        city: selectedAddress.city || '',
        postalcode: selectedAddress.postalCode || '',
        country: company.document.iso3166CountryCode || '',
      },
    ];

    const updatedContacts = [
      {
        contact_name: '',
        contact_email: email,
        contact_phone: phoneNumber,
        role: '',
      },
    ];

    setFieldValue('type', 'company');
    setFieldValue('orgnumber', company.document.registrationNumber || '');
    setFieldValue('vatnumber', company.document.isRegisteredForVAT ? `SE${company.document.registrationNumber}01` : '');
    setFieldValue('name', compName);
    setFieldValue('first_name', '');
    setFieldValue('last_name', '');
    setFieldValue('phone', phoneNumber);
    setFieldValue('payment_terms', '');
    setFieldValue('addresses', updatedAddresses);
    setFieldValue('contacts', updatedContacts);
  };

  return (
    <>
      <style>{`
        .customer-modal-content {
          border-radius: 8px;
          border: none;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        
        .customer-modal-header {
          background: white;
          color: #1e293b;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e2e8f0;
          position: relative;
        }
        
        .customer-modal-body {
          padding: 2rem;
          background: #ffffff;
          max-height: 70vh;
          overflow-y: auto;
        }
        
        .section-card {
          background: transparent;
          border: none;
          padding: 0;
          margin-bottom: 2rem;
        }
        
        .section-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
          padding: 0;
          border: none;
          background: transparent;
        }
        
        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }
        
        .section-subtitle {
          font-size: 0.875rem;
          color: #64748b;
          margin: 0;
        }
        
        .type-selector {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .type-option {
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
        }
        
        .type-option:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        
        .type-option.selected {
          border-color: var(--bs-primary);
          background: #eff7f1ff;
        }
        
        .type-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.75rem;
          background: #f1f5f9;
          color: #64748b;
          transition: all 0.2s ease;
        }
        
        .type-option.selected .type-icon {
          background: var(--bs-primary);
          color: white;
        }
        
        .type-label {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }
        
        .type-description {
          font-size: 0.875rem;
          color: #64748b;
        }
        
        .search-section {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }
        
        .search-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        
        .search-input-group {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        
        .search-input {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          background: white;
        }
        
        .search-input:focus {
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 2px rgba(22, 107, 72, 0.1);
        }
        
        .search-button {
          padding: 0.75rem 1.5rem;
          background: var(--bs-primary);
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }
        
        .search-button:hover {
          background: var(--bs-primary);
        }
        
        .search-button:disabled {
          background: #94a3b8;
          cursor: not-allowed;
        }
        
        .form-group {
          margin-bottom: 1.5rem;
        }
        
        .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }
        
        .form-label.required::after {
          content: " *";
          color: #ef4444;
        }
        
        .form-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }
        
        .form-input:focus {
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
        
        .form-input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.1);
        }
        
        .error-message {
          font-size: 0.75rem;
          color: #ef4444;
          margin-top: 0.25rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        
        .form-grid {
          display: grid;
          gap: 1rem;
        }
        
        .grid-2 {
          grid-template-columns: 1fr 1fr;
        }
        
        .grid-3 {
          grid-template-columns: 1fr 1fr 1fr;
        }
        
        .grid-4 {
          grid-template-columns: 1fr 1fr 1fr 1fr;
        }
        
        .address-card, .contact-card {
          background: transparent;
          border: none;
          border-radius: 0;
          padding: 0;
          margin-bottom: 2rem;
          position: relative;
        }
        
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .card-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }
        
        .remove-button {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
          border-radius: 4px;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .remove-button:hover {
          background: #fee2e2;
          border-color: #fca5a5;
        }
        
        .add-button {
          background: #f0fdf4;
          color: #16a34a;
          border: 1px solid #bbf7d0;
          border-radius: 6px;
          padding: 0.75rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          transition: all 0.2s ease;
          width: 100%;
          justify-content: center;
        }
        
        .add-button:hover {
          background: #dcfce7;
          border-color: #86efac;
        }
        
        .modal-actions {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .action-button {
          border-radius: 6px;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .btn-cancel {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        
        .btn-cancel:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .btn-save {
          background: var(--bs-primary);
          color: white;
        }
        
        .btn-save:hover {
          background: var(--bs-primary);
        }
        
        .close-button {
          position: absolute;
          top: 1rem;
          right: 1.5rem;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          width: 32px;
          height: 32px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .close-button:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(59, 130, 246, 0.3);
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .type-selector {
            grid-template-columns: 1fr;
          }
          
          .grid-2, .grid-3, .grid-4 {
            grid-template-columns: 1fr;
          }
          
          .search-input-group {
            flex-direction: column;
          }
        }
      `}</style>

      <Modal
        className='fade'
        size='lg'
        show={show}
        onHide={handleClose}
        backdrop="static"
        centered
      >


        <div className="customer-modal-content">
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              {/* Header */}
              <div className="customer-modal-header">
                <button type="button" className="close-button" onClick={handleClose}>
                  <X size={16} />
                </button>
                <div className="d-flex align-items-center gap-3">
                  {values.type === 'company' ? <Building size={24} /> : <User size={24} />}
                  <div>
                    <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                      {isEditMode ? 'Redigera kund' : 'Ny kund'}
                    </h3>
                    <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                      {isEditMode ? 'Uppdatera kundinformation' : 'Skapa en ny kund i systemet'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="customer-modal-body">
                {/* Customer Type Selection */}
                {!isEditMode && (
                  <div className="section-card">
                    <div className="section-header">
                      <Users size={20} />
                      <div>
                        <h4 className="section-title">Kundtyp</h4>
                        <p className="section-subtitle">Välj typ av kund du vill skapa</p>
                      </div>
                    </div>

                    <div className="type-selector">
                      <div
                        className={`type-option ${values.type === 'company' ? 'selected' : ''}`}
                        onClick={() => setFieldValue('type', 'company')}
                      >
                        <div className="type-icon">
                          <Building size={24} />
                        </div>
                        <div className="type-label">Företag</div>
                        <div className="type-description">Juridisk person med organisationsnummer</div>
                      </div>

                      <div
                        className={`type-option ${values.type === 'individual' ? 'selected' : ''}`}
                        onClick={() => setFieldValue('type', 'individual')}
                      >
                        <div className="type-icon">
                          <User size={24} />
                        </div>
                        <div className="type-label">Privatperson</div>
                        <div className="type-description">Fysisk person som privatperson</div>
                      </div>
                    </div>

                    {touched.type && errors.type && (
                      <div className="error-message">⚠ {errors.type}</div>
                    )}
                  </div>
                )}

                {/* Company Search */}
                {!isEditMode && values.type === 'company' && (
                  <div className="search-section">
                    <div className="search-header">
                      <Search size={20} />
                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>Företagssökning</h4>
                        <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                          Sök automatiskt efter företagsinformation
                        </p>
                      </div>
                    </div>

                    <div className="search-input-group">
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Ange företagsnamn eller organisationsnummer"
                        value={values.name || ''}
                        onChange={(e) => setFieldValue('name', e.target.value)}
                      />
                      <button
                        type="button"
                        className="search-button"
                        onClick={() => searchCompany(values.name)}
                        disabled={searchLoading || !values.name}
                      >
                        {searchLoading ? (
                          <>
                            <div className="loading-spinner"></div>
                            Söker...
                          </>
                        ) : (
                          <>
                            <Search size={16} />
                            Sök företag
                          </>
                        )}
                      </button>
                    </div>

                    {companies.length > 0 && (
                      <div>
                        <label className="form-label">Hittade {companies.length} företag</label>
                        <Select
                          options={companies}
                          onChange={handleCompanySelect}
                          placeholder="Välj ett företag från sökresultatet"
                          className="react-select-container"
                          classNamePrefix="react-select"
                          value={
                            selectedCompany
                              ? {
                                label: selectedCompany.document.names[0].nameOrIdentifier,
                                value: selectedCompany
                              }
                              : null
                          }
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              padding: '0.25rem',
                            }),
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Basic Information */}
                <div className="section-card">
                  <div className="section-header">
                    {values.type === 'company' ? <Building size={20} /> : <User size={20} />}
                    <div>
                      <h4 className="section-title">Grundläggande information</h4>
                      <p className="section-subtitle">
                        {values.type === 'company' ? 'Företagsinformation' : 'Personuppgifter'}
                      </p>
                    </div>
                  </div>

                  {values.type === 'company' ? (
                    <div className="form-grid grid-2">
                      <div className="form-group">
                        <label className="form-label required">Företagsnamn</label>
                        <input
                          type="text"
                          className={`form-input ${touched.name && errors.name ? 'error' : ''}`}
                          value={values.name || ''}
                          onChange={(e) => setFieldValue('name', e.target.value)}
                          placeholder="Ange företagsnamn"
                        />
                        {touched.name && errors.name && (
                          <div className="error-message">⚠ {errors.name}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label required">Organisationsnummer</label>
                        <input
                          type="text"
                          className={`form-input ${touched.orgnumber && errors.orgnumber ? 'error' : ''}`}
                          value={values.orgnumber || ''}
                          onChange={(e) => setFieldValue('orgnumber', e.target.value)}
                          placeholder="XXXXXX-XXXX"
                        />
                        {touched.orgnumber && errors.orgnumber && (
                          <div className="error-message">⚠ {errors.orgnumber}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">VAT-nummer</label>
                        <input
                          type="text"
                          className="form-input"
                          value={values.vatnumber || ''}
                          onChange={(e) => setFieldValue('vatnumber', e.target.value)}
                          placeholder="SEXXXXXX01"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label required">Telefonnummer</label>
                        <input
                          type="text"
                          className={`form-input ${touched.phone && errors.phone ? 'error' : ''}`}
                          value={values.phone || ''}
                          onChange={(e) => setFieldValue('phone', e.target.value)}
                          placeholder="+46 XX XXX XX XX"
                        />
                        {touched.phone && errors.phone && (
                          <div className="error-message">⚠ {errors.phone}</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="form-grid grid-2">
                      <div className="form-group">
                        <label className="form-label required">Förnamn</label>
                        <input
                          type="text"
                          className={`form-input ${touched.first_name && errors.first_name ? 'error' : ''}`}
                          value={values.first_name || ''}
                          onChange={(e) => setFieldValue('first_name', e.target.value)}
                          placeholder="Ange förnamn"
                        />
                        {touched.first_name && errors.first_name && (
                          <div className="error-message">⚠ {errors.first_name}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label required">Efternamn</label>
                        <input
                          type="text"
                          className={`form-input ${touched.last_name && errors.last_name ? 'error' : ''}`}
                          value={values.last_name || ''}
                          onChange={(e) => setFieldValue('last_name', e.target.value)}
                          placeholder="Ange efternamn"
                        />
                        {touched.last_name && errors.last_name && (
                          <div className="error-message">⚠ {errors.last_name}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label required">Telefonnummer</label>
                        <input
                          type="text"
                          className={`form-input ${touched.phone && errors.phone ? 'error' : ''}`}
                          value={values.phone || ''}
                          onChange={(e) => setFieldValue('phone', e.target.value)}
                          placeholder="+46 XX XXX XX XX"
                        />
                        {touched.phone && errors.phone && (
                          <div className="error-message">⚠ {errors.phone}</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Betalningsvillkor (dagar)</label>
                    <input
                      type="number"
                      className={`form-input ${touched.payment_terms && errors.payment_terms ? 'error' : ''}`}
                      value={values.payment_terms || ''}
                      onChange={(e) => setFieldValue('payment_terms', e.target.value)}
                      placeholder="30"
                      style={{ maxWidth: '200px' }}
                    />
                    {touched.payment_terms && errors.payment_terms && (
                      <div className="error-message">⚠ {errors.payment_terms}</div>
                    )}
                  </div>
                </div>

                {/* Addresses */}
                <div className="section-card">
                  <div className="section-header">
                    <MapPin size={20} />
                    <div>
                      <h4 className="section-title">Adresser</h4>
                      <p className="section-subtitle">Faktura- och leveransadresser</p>
                    </div>
                  </div>

                  <FieldArray name="addresses">
                    {({ push, remove }) => (
                      <>
                        {values.addresses.map((addr, idx) => (
                          <div key={idx} className="address-card">
                            <div className="card-header">
                              <h6 className="card-title">Adress {idx + 1}</h6>
                              {values.addresses.length > 1 && (
                                <button
                                  type="button"
                                  className="remove-button"
                                  onClick={() => remove(idx)}
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>

                            <div className="form-grid grid-4">
                              <div className="form-group">
                                <label className="form-label required">Typ</label>
                                <Select
                                  options={addressTypeOptions}
                                  value={addressTypeOptions.find(o => o.value === addr.address_type)}
                                  onChange={(opt) => setFieldValue(`addresses.${idx}.address_type`, opt.value)}
                                  placeholder="Välj typ"
                                  styles={{
                                    control: (provided) => ({
                                      ...provided,
                                      border: '1px solid #d1d5db',
                                      borderRadius: '8px',
                                      minHeight: '46px',
                                    }),
                                  }}
                                />
                                {touched.addresses?.[idx]?.address_type && errors.addresses?.[idx]?.address_type && (
                                  <div className="error-message">⚠ {errors.addresses[idx].address_type}</div>
                                )}
                              </div>
                            </div>

                            <div className="form-grid grid-2">
                              <div className="form-group">
                                <label className="form-label required">Gatuadress</label>
                                <input
                                  type="text"
                                  className={`form-input ${touched.addresses?.[idx]?.street1 && errors.addresses?.[idx]?.street1 ? 'error' : ''}`}
                                  value={addr.street1 || ''}
                                  onChange={(e) => setFieldValue(`addresses.${idx}.street1`, e.target.value)}
                                  placeholder="Gatunamn och nummer"
                                />
                                {touched.addresses?.[idx]?.street1 && errors.addresses?.[idx]?.street1 && (
                                  <div className="error-message">⚠ {errors.addresses[idx].street1}</div>
                                )}
                              </div>
                            </div>

                            <div className="form-grid grid-3">
                              <div className="form-group">
                                <label className="form-label required">Ort</label>
                                <input
                                  type="text"
                                  className={`form-input ${touched.addresses?.[idx]?.city && errors.addresses?.[idx]?.city ? 'error' : ''}`}
                                  value={addr.city || ''}
                                  onChange={(e) => setFieldValue(`addresses.${idx}.city`, e.target.value)}
                                  placeholder="Ort"
                                />
                                {touched.addresses?.[idx]?.city && errors.addresses?.[idx]?.city && (
                                  <div className="error-message">⚠ {errors.addresses[idx].city}</div>
                                )}
                              </div>

                              <div className="form-group">
                                <label className="form-label required">Postnummer</label>
                                <input
                                  type="text"
                                  className={`form-input ${touched.addresses?.[idx]?.postalcode && errors.addresses?.[idx]?.postalcode ? 'error' : ''}`}
                                  value={addr.postalcode || ''}
                                  onChange={(e) => setFieldValue(`addresses.${idx}.postalcode`, e.target.value)}
                                  placeholder="XXX XX"
                                />
                                {touched.addresses?.[idx]?.postalcode && errors.addresses?.[idx]?.postalcode && (
                                  <div className="error-message">⚠ {errors.addresses[idx].postalcode}</div>
                                )}
                              </div>

                              <div className="form-group">
                                <label className="form-label">Land</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={addr.country || ''}
                                  onChange={(e) => setFieldValue(`addresses.${idx}.country`, e.target.value)}
                                  placeholder="Sverige"
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          className="add-button"
                          onClick={() => push({
                            address_type: 'billing',
                            street1: '',
                            street2: '',
                            city: '',
                            postalcode: '',
                            country: '',
                          })}
                        >
                          <Plus size={16} />
                          Lägg till ny adress
                        </button>

                        {touched.addresses && typeof errors.addresses === 'string' && (
                          <div className="error-message">⚠ {errors.addresses}</div>
                        )}
                      </>
                    )}
                  </FieldArray>
                </div>

                {/* Contacts for Companies */}
                {values.type === 'company' && (
                  <div className="section-card">
                    <div className="section-header">
                      <Users size={20} />
                      <div>
                        <h4 className="section-title">Kontaktpersoner</h4>
                        <p className="section-subtitle">Kontaktuppgifter för företaget</p>
                      </div>
                    </div>

                    <FieldArray name="contacts">
                      {({ push, remove }) => (
                        <>
                          {values.contacts.map((contact, index) => (
                            <div key={index} className="contact-card">
                              <div className="card-header">
                                <h6 className="card-title">Kontakt {index + 1}</h6>
                                {values.contacts.length > 1 && (
                                  <button
                                    type="button"
                                    className="remove-button"
                                    onClick={() => remove(index)}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </div>

                              <div className="form-grid grid-3">
                                <div className="form-group">
                                  <label className="form-label required">Namn</label>
                                  <input
                                    type="text"
                                    className={`form-input ${touched.contacts?.[index]?.contact_name && errors.contacts?.[index]?.contact_name ? 'error' : ''}`}
                                    value={contact.contact_name || ''}
                                    onChange={(e) => setFieldValue(`contacts.${index}.contact_name`, e.target.value)}
                                    placeholder="Kontaktpersonens namn"
                                  />
                                  {touched.contacts?.[index]?.contact_name && errors.contacts?.[index]?.contact_name && (
                                    <div className="error-message">⚠ {errors.contacts[index].contact_name}</div>
                                  )}
                                </div>

                                <div className="form-group">
                                  <label className="form-label">E-post</label>
                                  <input
                                    type="email"
                                    className={`form-input ${touched.contacts?.[index]?.contact_email && errors.contacts?.[index]?.contact_email ? 'error' : ''}`}
                                    value={contact.contact_email || ''}
                                    onChange={(e) => setFieldValue(`contacts.${index}.contact_email`, e.target.value)}
                                    placeholder="kontakt@företag.se"
                                  />
                                  {touched.contacts?.[index]?.contact_email && errors.contacts?.[index]?.contact_email && (
                                    <div className="error-message">⚠ {errors.contacts[index].contact_email}</div>
                                  )}
                                </div>

                                <div className="form-group">
                                  <label className="form-label">Telefon</label>
                                  <input
                                    type="text"
                                    className={`form-input ${touched.contacts?.[index]?.contact_phone && errors.contacts?.[index]?.contact_phone ? 'error' : ''}`}
                                    value={contact.contact_phone || ''}
                                    onChange={(e) => setFieldValue(`contacts.${index}.contact_phone`, e.target.value)}
                                    placeholder="+46 XX XXX XX XX"
                                  />
                                  {touched.contacts?.[index]?.contact_phone && errors.contacts?.[index]?.contact_phone && (
                                    <div className="error-message">⚠ {errors.contacts[index].contact_phone}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="add-button"
                            onClick={() => push({ contact_name: '', contact_email: '', contact_phone: '', role: '' })}
                          >
                            <Plus size={16} />
                            Lägg till ny kontaktperson
                          </button>

                          {touched.contacts && typeof errors.contacts === 'string' && (
                            <div className="error-message">⚠ {errors.contacts}</div>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="modal-actions">
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  {isEditMode ? 'Ändringar sparas automatiskt' : 'Alla obligatoriska fält måste fyllas i'}
                </div>
                <div className="d-flex gap-3">
                  <button
                    type="button"
                    className="action-button btn-cancel"
                    onClick={handleClose}
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    className="action-button btn-save"
                  >
                    {isEditMode ? (
                      <>
                        <Building size={16} />
                        Uppdatera kund
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Skapa kund
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </Modal>
    </>
  );
};

export default NewCustomerModal;




// import React, { useState, useEffect } from 'react';
// import { Modal, Button } from 'react-bootstrap';
// import Select from 'react-select';
// import axios from '../../../../../services/AxiosInstance';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
// import swal from 'sweetalert';
// import { getApiUrl } from '../../../../../utils/ApiUtils';
// import { useFormik, FormikProvider, FieldArray } from 'formik';
// import * as Yup from 'yup';

// const typeOptions = [
//   { value: 'company', label: 'Företag' },
//   { value: 'individual', label: 'Privatperson' },
// ];

// const addressTypeOptions = [
//   { value: 'billing', label: 'Fakturaadress' },
//   { value: 'shipping', label: 'Leveransadress' },
//   { value: 'other', label: 'Annan adress' },
// ];

// // Validation Schema with conditions
// const validationSchema = Yup.object().shape({
//   type: Yup.string().required('Kundtyp måste väljas'),
//   // For company: name and orgnumber required
//   // For individual: first_name and last_name required
//   name: Yup.string().when('type', {
//     is: 'company',
//     then: Yup.string().required('Företagsnamn krävs för företag'),
//     otherwise: Yup.string().notRequired(),
//   }),
//   orgnumber: Yup.string().when('type', {
//     is: 'company',
//     then: Yup.string().required('Organisationsnummer krävs för företag'),
//     otherwise: Yup.string().notRequired(),
//   }),
//   first_name: Yup.string().when('type', {
//     is: 'individual',
//     then: Yup.string().required('Förnamn krävs för privatperson'),
//     otherwise: Yup.string().notRequired(),
//   }),
//   last_name: Yup.string().when('type', {
//     is: 'individual',
//     then: Yup.string().required('Efternamn krävs för privatperson'),
//     otherwise: Yup.string().notRequired(),
//   }),
//   phone: Yup.string().required('Telefonnummer är obligatoriskt'),
//   payment_terms: Yup.number().transform(value => (isNaN(value) ? undefined : value))
//     .nullable(true)
//     .notRequired(),
//   addresses: Yup.array().of(
//     Yup.object().shape({
//       address_type: Yup.string().oneOf(['billing', 'shipping', 'other']).required(),
//       street1: Yup.string().required('Gatuadress krävs'),
//       city: Yup.string().required('Ort krävs'),
//       postalcode: Yup.string().required('Postnummer krävs'),
//       country: Yup.string().nullable(),
//     })
//   ).min(1, 'Minst en adress måste anges'),
//   contacts: Yup.array().when('type', {
//     is: 'company',
//     then: Yup.array().of(
//       Yup.object().shape({
//         contact_name: Yup.string().required('Kontaktpersonens namn krävs för företag'),
//         contact_email: Yup.string().email('Ogiltig e-post').nullable(),
//         contact_phone: Yup.string().nullable(),
//         role: Yup.string().nullable(),
//       })
//     ).min(1, 'Minst en kontaktperson måste anges för ett företag'),
//     otherwise: Yup.array().of(
//       Yup.object().shape({
//         // For individuals, contacts are optional, but if present, can validate lightly
//         contact_name: Yup.string().nullable(),
//         contact_email: Yup.string().email('Ogiltig e-post').nullable(),
//         contact_phone: Yup.string().nullable(),
//         role: Yup.string().nullable(),
//       })
//     )
//   })
// });

// const NewCustomerModal = ({ show, handleClose, handleSave, handleUpdate, customerDetails }) => {
//   const isEditMode = !!customerDetails;

//   const defaultValues = {
//     id: '',
//     type: 'company',
//     orgnumber: '',
//     vatnumber: '',
//     name: '',
//     first_name: '',
//     last_name: '',
//     phone: '',
//     payment_terms: '',
//     addresses: [
//       {
//         address_type: 'billing',
//         street1: '',
//         street2: '',
//         city: '',
//         postalcode: '',
//         country: '',
//       },
//     ],
//     contacts: [
//       {
//         contact_name: '',
//         contact_email: '',
//         contact_phone: '',
//         role: '',
//       },
//     ],
//   };

//   const [companies, setCompanies] = useState([]);
//   const [selectedCompany, setSelectedCompany] = useState(null);

//   const formik = useFormik({
//     initialValues: defaultValues,
//     validationSchema: validationSchema,
//     onSubmit: (values) => {
//       const payload = {
//         type: values.type,
//         orgnumber: values.type === 'company' ? values.orgnumber || '' : '',
//         vatnumber: values.type === 'company' ? values.vatnumber || '' : '',
//         name: values.type === 'company' ? values.name || '' : '',
//         first_name: values.type === 'individual' ? values.first_name || '' : '',
//         last_name: values.type === 'individual' ? values.last_name || '' : '',
//         phone: values.phone || '',
//         payment_terms: values.payment_terms || '',
//         addresses: values.addresses.map((addr) => ({
//           address_type: addr.address_type,
//           street1: addr.street1 || '',
//           street2: addr.street2 || '',
//           city: addr.city || '',
//           postalcode: addr.postalcode || '',
//           country: addr.country || '',
//         })),
//         contacts: values.contacts.map((ct) => ({
//           contact_name: ct.contact_name || '',
//           contact_email: ct.contact_email || '',
//           contact_phone: ct.contact_phone || '',
//           role: ct.role || '',
//         })),
//       };

//       if (isEditMode) {
//         payload.id = values.id;
//         handleUpdate(payload);
//       } else {
//         handleSave(payload);
//       }
//       handleClose();
//     }
//   });

//   const { values, errors, touched, setFieldValue } = formik;

//   useEffect(() => {
//     if (show) {
//       if (isEditMode && customerDetails) {
//         const {
//           id,
//           type,
//           orgnumber,
//           vatnumber,
//           name,
//           first_name,
//           last_name,
//           phone,
//           payment_terms,
//           addresses = [],
//           contacts = [],
//         } = customerDetails;

//         formik.resetForm({
//           values: {
//             id: id || '',
//             type: type || 'company',
//             orgnumber: orgnumber || '',
//             vatnumber: vatnumber || '',
//             name: name || '',
//             first_name: first_name || '',
//             last_name: last_name || '',
//             phone: phone || '',
//             payment_terms: payment_terms || '',
//             addresses: addresses.length > 0 ? addresses : [
//               {
//                 address_type: 'billing',
//                 street1: '',
//                 street2: '',
//                 city: '',
//                 postalcode: '',
//                 country: '',
//               },
//             ],
//             contacts: contacts.length > 0 ? contacts : [
//               {
//                 contact_name: '',
//                 contact_email: '',
//                 contact_phone: '',
//                 role: '',
//               },
//             ],
//           }
//         });
//       } else {
//         formik.resetForm({ values: defaultValues });
//       }
//       setCompanies([]);
//       setSelectedCompany(null);
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [show, isEditMode, customerDetails]);

//   const searchCompany = async (inputValue) => {
//     if (!inputValue) return;

//     try {
//       const searchCompanyApiUrl = getApiUrl('generic/search-company');
//       const response = await axios.get(searchCompanyApiUrl, {
//         params: { query: inputValue },
//       });

//       setCompanies(
//         response.data.hits.map((company) => ({
//           label: `${company.document.names[0].nameOrIdentifier} (${company.document.registrationNumber})`,
//           value: company,
//         }))
//       );
//     } catch (err) {
//       console.error('Error fetching company data:', err.message);
//       swal(
//         'Fel',
//         'Kunde inte hämta företagsinformation, försök igen senare eller ange informationen manuellt.',
//         'error'
//       );
//     }
//   };

//   const handleCompanySelect = (selectedOption) => {
//     const company = selectedOption.value;
//     setSelectedCompany(company);

//     const visitingAddress = company.document.addresses?.find(addr => addr.addressType === 'registeredAddress');
//     const mailAddress = company.document.addresses?.find(addr => addr.addressType === 'mailAddress');
//     const selectedAddress = visitingAddress || mailAddress || {};

//     const phoneNumber = company.document.phoneNumbers?.[0]?.e164PhoneNumber || '';
//     const email = company.document.emailAddresses?.[0].emailAddress || '';
//     const compName = company.document.names?.[0]?.nameOrIdentifier || '';

//     const updatedAddresses = [
//       {
//         address_type: 'billing',
//         street1: ((selectedAddress.street || '') + ' ' + (selectedAddress.houseNumber || '')).trim(),
//         street2: '',
//         city: selectedAddress.city || '',
//         postalcode: selectedAddress.postalCode || '',
//         country: company.document.iso3166CountryCode || '',
//       },
//     ];

//     const updatedContacts = [
//       {
//         contact_name: '',
//         contact_email: email,
//         contact_phone: phoneNumber,
//         role: '',
//       },
//     ];

//     // Set all these values in formik
//     setFieldValue('type', 'company');
//     setFieldValue('orgnumber', company.document.registrationNumber || '');
//     setFieldValue('vatnumber', company.document.isRegisteredForVAT ? `SE${company.document.registrationNumber}01` : '');
//     setFieldValue('name', compName);
//     setFieldValue('first_name', '');
//     setFieldValue('last_name', '');
//     setFieldValue('phone', phoneNumber);
//     setFieldValue('payment_terms', '');
//     setFieldValue('addresses', updatedAddresses);
//     setFieldValue('contacts', updatedContacts);
//   };

//   return (
//     <Modal
//       className='fade bd-example-modal-lg'
//       size='lg'
//       show={show}
//       onHide={handleClose}
//     >
//       <FormikProvider value={formik}>
//         <form onSubmit={formik.handleSubmit}>
//           <Modal.Header closeButton>
//             <Modal.Title>{isEditMode ? 'Redigera kund' : 'Ny kund'}</Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             <div className="basic-form">
//               <div className="col-xl-12">
//                 {!isEditMode && (
//                   <>
//                     <label>Typ av kund</label>
//                     <Select
//                       options={typeOptions}
//                       value={typeOptions.find((opt) => opt.value === values.type)}
//                       onChange={(selected) => setFieldValue('type', selected.value)}
//                       className="mb-3"
//                     />
//                     {touched.type && errors.type && <div className="text-danger">{errors.type}</div>}
//                   </>
//                 )}

//                 {!isEditMode && values.type === 'company' && (
//                   <>
//                     <label>Sök företag</label>
//                     <div className="input-group mb-3">
//                       <input
//                         type="text"
//                         className="form-control"
//                         placeholder="Ange företagsnamn eller org-nummer"
//                         value={values.name || ''}
//                         onChange={(e) => setFieldValue('name', e.target.value)}
//                       />
//                       <Button variant="primary" onClick={() => searchCompany(values.name)}>
//                         Hämta företagsinformation
//                       </Button>
//                     </div>
//                     {companies.length > 0 && (
//                       <>
//                         <label>Hittade {companies.length} företag</label>
//                         <Select
//                           options={companies}
//                           onChange={handleCompanySelect}
//                           placeholder="Välj ett företag"
//                           className='mb-5'
//                           value={
//                             selectedCompany
//                               ? {
//                                 label: selectedCompany.document.names[0].nameOrIdentifier,
//                                 value: selectedCompany
//                               }
//                               : null
//                           }
//                         />
//                       </>
//                     )}
//                   </>
//                 )}

//                 {/* Company fields */}
//                 {values.type === 'company' && (
//                   <>
//                     <div className="row">
//                       <div className="form-group mb-3 col-md-6">
//                         <label>Företagsnamn *</label>
//                         <input
//                           type="text"
//                           className="form-control"
//                           value={values.name || ''}
//                           onChange={(e) => setFieldValue('name', e.target.value)}
//                         />
//                         {touched.name && errors.name && <div className="text-danger">{errors.name}</div>}
//                       </div>
//                       <div className="form-group mb-3 col-md-6">
//                         <label>Org-nummer *</label>
//                         <input
//                           type="text"
//                           className="form-control"
//                           value={values.orgnumber || ''}
//                           onChange={(e) => setFieldValue('orgnumber', e.target.value)}
//                         />
//                         {touched.orgnumber && errors.orgnumber && <div className="text-danger">{errors.orgnumber}</div>}
//                       </div>
//                     </div>
//                     <div className="row">
//                       <div className="form-group mb-3 col-md-6">
//                         <label>Vat-nummer</label>
//                         <input
//                           type="text"
//                           className="form-control"
//                           value={values.vatnumber || ''}
//                           onChange={(e) => setFieldValue('vatnumber', e.target.value)}
//                         />
//                       </div>
//                       <div className="form-group mb-3 col-md-6">
//                         <label>Telefonnummer *</label>
//                         <input
//                           type="text"
//                           className="form-control"
//                           value={values.phone || ''}
//                           onChange={(e) => setFieldValue('phone', e.target.value)}
//                         />
//                         {touched.phone && errors.phone && <div className="text-danger">{errors.phone}</div>}
//                       </div>
//                     </div>
//                   </>
//                 )}

//                 {/* Individual fields */}
//                 {values.type === 'individual' && (
//                   <>
//                     <div className="row">
//                       <div className="form-group mb-3 col-md-6">
//                         <label>Förnamn *</label>
//                         <input
//                           type="text"
//                           className="form-control"
//                           value={values.first_name || ''}
//                           onChange={(e) => setFieldValue('first_name', e.target.value)}
//                         />
//                         {touched.first_name && errors.first_name && <div className="text-danger">{errors.first_name}</div>}
//                       </div>
//                       <div className="form-group mb-3 col-md-6">
//                         <label>Efternamn *</label>
//                         <input
//                           type="text"
//                           className="form-control"
//                           value={values.last_name || ''}
//                           onChange={(e) => setFieldValue('last_name', e.target.value)}
//                         />
//                         {touched.last_name && errors.last_name && <div className="text-danger">{errors.last_name}</div>}
//                       </div>
//                     </div>
//                     <div className="row">
//                       <div className="form-group mb-3 col-md-6">
//                         <label>Telefonnummer *</label>
//                         <input
//                           type="text"
//                           className="form-control"
//                           value={values.phone || ''}
//                           onChange={(e) => setFieldValue('phone', e.target.value)}
//                         />
//                         {touched.phone && errors.phone && <div className="text-danger">{errors.phone}</div>}
//                       </div>
//                     </div>
//                   </>
//                 )}

//                 <div className="row">
//                   <div className="form-group mb-3 col-md-6">
//                     <label>Betalningsvillkor (dagar)</label>
//                     <input
//                       type="number"
//                       className="form-control"
//                       value={values.payment_terms || ''}
//                       onChange={(e) => setFieldValue('payment_terms', e.target.value)}
//                     />
//                     {touched.payment_terms && errors.payment_terms && <div className="text-danger">{errors.payment_terms}</div>}
//                   </div>
//                 </div>

//                 <hr />
//                 <h5>Adresser</h5>
//                 <FieldArray name="addresses">
//                   {({ push, remove }) => (
//                     <>
//                       {values.addresses.map((addr, idx) => (
//                         <div key={idx} className="mb-4 border p-3">
//                           <div className="d-flex justify-content-between align-items-center mb-2">
//                             <h6>Adress {idx + 1}</h6>
//                             {values.addresses.length > 1 && (
//                               <Button variant="link" onClick={() => remove(idx)}>
//                                 <FontAwesomeIcon icon={faTrash} />
//                               </Button>
//                             )}
//                           </div>
//                           <div className="row">
//                             <div className="form-group mb-3 col-md-4">
//                               <label>Typ *</label>
//                               <Select
//                                 options={addressTypeOptions}
//                                 value={addressTypeOptions.find(o => o.value === addr.address_type)}
//                                 onChange={(opt) => setFieldValue(`addresses.${idx}.address_type`, opt.value)}
//                               />
//                               {touched.addresses?.[idx]?.address_type && errors.addresses?.[idx]?.address_type && (
//                                 <div className="text-danger">{errors.addresses[idx].address_type}</div>
//                               )}
//                             </div>
//                           </div>
//                           <div className="row">
//                             <div className="form-group mb-3 col-md-8">
//                               <label>Gata *</label>
//                               <input
//                                 type="text"
//                                 className="form-control"
//                                 value={addr.street1 || ''}
//                                 onChange={(e) => setFieldValue(`addresses.${idx}.street1`, e.target.value)}
//                               />
//                               {touched.addresses?.[idx]?.street1 && errors.addresses?.[idx]?.street1 && (
//                                 <div className="text-danger">{errors.addresses[idx].street1}</div>
//                               )}
//                             </div>
//                           </div>
//                           <div className="row">
//                             <div className="form-group mb-3 col-md-4">
//                               <label>Ort *</label>
//                               <input
//                                 type="text"
//                                 className="form-control"
//                                 value={addr.city || ''}
//                                 onChange={(e) => setFieldValue(`addresses.${idx}.city`, e.target.value)}
//                               />
//                               {touched.addresses?.[idx]?.city && errors.addresses?.[idx]?.city && (
//                                 <div className="text-danger">{errors.addresses[idx].city}</div>
//                               )}
//                             </div>
//                             <div className="form-group mb-3 col-md-4">
//                               <label>Postnummer *</label>
//                               <input
//                                 type="text"
//                                 className="form-control"
//                                 value={addr.postalcode || ''}
//                                 onChange={(e) => setFieldValue(`addresses.${idx}.postalcode`, e.target.value)}
//                               />
//                               {touched.addresses?.[idx]?.postalcode && errors.addresses?.[idx]?.postalcode && (
//                                 <div className="text-danger">{errors.addresses[idx].postalcode}</div>
//                               )}
//                             </div>
//                           </div>
//                           <div className="row">
//                             <div className="form-group mb-3 col-md-4">
//                               <label>Land</label>
//                               <input
//                                 type="text"
//                                 className="form-control"
//                                 value={addr.country || ''}
//                                 onChange={(e) => setFieldValue(`addresses.${idx}.country`, e.target.value)}
//                               />
//                             </div>
//                           </div>
//                         </div>
//                       ))}
//                       <Button variant="link" onClick={() => push({
//                         address_type: 'billing',
//                         street1: '',
//                         street2: '',
//                         city: '',
//                         postalcode: '',
//                         country: '',
//                       })}>
//                         <FontAwesomeIcon icon={faPlus} className="me-2" />Lägg till ny adress
//                       </Button>
//                       {touched.addresses && typeof errors.addresses === 'string' && (
//                         <div className="text-danger">{errors.addresses}</div>
//                       )}
//                     </>
//                   )}
//                 </FieldArray>

//                 {values.type === 'company' && (
//                   <>
//                     <hr />
//                     <h5>Kontaktpersoner</h5>
//                     <FieldArray name="contacts">
//                       {({ push, remove }) => (
//                         <>
//                           {values.contacts.map((contact, index) => (
//                             <div key={index} className="mb-4 border p-3">
//                               <div className="d-flex justify-content-between align-items-center mb-2">
//                                 <h6>Kontakt {index + 1}</h6>
//                                 {values.contacts.length > 1 && (
//                                   <Button variant="link" onClick={() => remove(index)}>
//                                     <FontAwesomeIcon icon={faTrash} />
//                                   </Button>
//                                 )}
//                               </div>
//                               <div className="row">
//                                 <div className="form-group mb-3 col-md-4">
//                                   <label>Namn *</label>
//                                   <input
//                                     type="text"
//                                     className="form-control"
//                                     value={contact.contact_name || ''}
//                                     onChange={(e) => setFieldValue(`contacts.${index}.contact_name`, e.target.value)}
//                                   />
//                                   {touched.contacts?.[index]?.contact_name && errors.contacts?.[index]?.contact_name && (
//                                     <div className="text-danger">{errors.contacts[index].contact_name}</div>
//                                   )}
//                                 </div>
//                                 <div className="form-group mb-3 col-md-4">
//                                   <label>E-post</label>
//                                   <input
//                                     type="email"
//                                     className="form-control"
//                                     value={contact.contact_email || ''}
//                                     onChange={(e) => setFieldValue(`contacts.${index}.contact_email`, e.target.value)}
//                                   />
//                                   {touched.contacts?.[index]?.contact_email && errors.contacts?.[index]?.contact_email && (
//                                     <div className="text-danger">{errors.contacts[index].contact_email}</div>
//                                   )}
//                                 </div>
//                                 <div className="form-group mb-3 col-md-3">
//                                   <label>Telefon</label>
//                                   <input
//                                     type="text"
//                                     className="form-control"
//                                     value={contact.contact_phone || ''}
//                                     onChange={(e) => setFieldValue(`contacts.${index}.contact_phone`, e.target.value)}
//                                   />
//                                   {touched.contacts?.[index]?.contact_phone && errors.contacts?.[index]?.contact_phone && (
//                                     <div className="text-danger">{errors.contacts[index].contact_phone}</div>
//                                   )}
//                                 </div>
//                               </div>
//                             </div>
//                           ))}
//                           <Button variant="link" onClick={() => push({ contact_name: '', contact_email: '', contact_phone: '', role: '' })}>
//                             <FontAwesomeIcon icon={faPlus} className="me-2" />Lägg till ny kontaktperson
//                           </Button>
//                           {touched.contacts && typeof errors.contacts === 'string' && (
//                             <div className="text-danger">{errors.contacts}</div>
//                           )}
//                         </>
//                       )}
//                     </FieldArray>
//                   </>
//                 )}

//               </div>
//             </div>
//           </Modal.Body>
//           <Modal.Footer>
//             <Button variant="secondary" onClick={handleClose}>
//               Avbryt
//             </Button>
//             <Button variant="primary" type="submit">
//               {isEditMode ? 'Uppdatera' : 'Spara'}
//             </Button>
//           </Modal.Footer>
//         </form>
//       </FormikProvider>
//     </Modal>
//   );
// };

// export default NewCustomerModal;
